/**
 * PresenceBar.js
 * 
 * Displays colored avatar circles for each user currently viewing this record.
 * Shows a green dot connection indicator and tooltips with full names.
 * 
 * Drop into: /root/legalflow/frontend/src/components/record/PresenceBar.js
 */
import React, { useState } from 'react';
import { Wifi, WifiOff } from 'lucide-react';

export default function PresenceBar({ users = [], isConnected }) {
  const [hoveredUser, setHoveredUser] = useState(null);

  if (!users.length && isConnected) return null;

  return (
    <div className="flex items-center gap-2">
      {/* Connection indicator */}
      <div className="flex items-center gap-1.5 mr-1">
        {isConnected ? (
          <Wifi size={14} className="text-emerald-500" />
        ) : (
          <WifiOff size={14} className="text-gray-400" />
        )}
        {!isConnected && (
          <span className="text-xs text-gray-400">Reconnecting…</span>
        )}
      </div>

      {/* User avatars */}
      <div className="flex -space-x-2">
        {users.slice(0, 5).map((user) => (
          <div
            key={user.userId}
            className="relative"
            onMouseEnter={() => setHoveredUser(user.userId)}
            onMouseLeave={() => setHoveredUser(null)}
          >
            {/* Avatar circle */}
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold ring-2 ring-white cursor-default transition-transform hover:scale-110 hover:z-10"
              style={{ backgroundColor: user.color || '#6B7280' }}
              title={`${user.firstName} ${user.lastName}`}
            >
              {getInitials(user.firstName, user.lastName)}
            </div>

            {/* Active pulse */}
            <span
              className="absolute bottom-0 right-0 w-2 h-2 rounded-full ring-1 ring-white bg-emerald-400"
              style={{ animation: 'pulse 2s infinite' }}
            />

            {/* Tooltip */}
            {hoveredUser === user.userId && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-50">
                {user.firstName} {user.lastName}
                <div className="text-gray-400 text-[10px]">Editing now</div>
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
              </div>
            )}
          </div>
        ))}

        {/* Overflow indicator */}
        {users.length > 5 && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-200 text-gray-600 text-xs font-semibold ring-2 ring-white">
            +{users.length - 5}
          </div>
        )}
      </div>

      {/* Count label */}
      {users.length > 0 && (
        <span className="text-xs text-gray-500 ml-1">
          {users.length} {users.length === 1 ? 'other' : 'others'} viewing
        </span>
      )}
    </div>
  );
}

function getInitials(firstName, lastName) {
  return `${(firstName || '?')[0]}${(lastName || '')[0] || ''}`.toUpperCase();
}
